import { useStore } from 'zustand';
import { useCallback, useMemo } from 'react';

import { SearchType } from '@/types/most-search-type';
import { SelectedProcedures } from '@/app/(main)/_components/search-modal/search-modal.types';
import { SearchResult } from '@/app/(main)/_components/search-modal/search-modal.types';
import { formatToTitleCase } from '@/utils/formatters/format-to-title-case';
import { userLocationStore } from '@/store/user-location.store';
import { OverridedUserLocation } from '@/utils/formatters/city/format-place-from-slug.const';
import { useSearchParams } from '@/hooks/use-search-params.hook';
import { useNavigation } from '@/hooks/navigation/use-navigation.hook';
import { useAgendamentoNavigation } from '@/hooks/navigation/use-agendamento-navigation.hook';
import { PersistedUserNavigationSearchParams } from '@/types/enums/persisted-user-navigation-search-params.enum';
import { buildPathWithQuery } from '@/utils/persisted-params.util';
import { isProcedureSearch } from '@/app/_utils/is-procedure-search.util';
import { EnvironmentNameEnum, getEnvironmentName } from '@/config/environment';

interface UseSearchProps {
  locationOverride?: OverridedUserLocation;
}

export function useSearch({ locationOverride }: UseSearchProps = {}) {
  const isProduction = useMemo(() => {
    if (typeof window === 'undefined') {
      return false;
    }
    return getEnvironmentName() === EnvironmentNameEnum.PRODUCTION;
  }, []);

  const userStore = useStore(userLocationStore, (state) => state.userLocation);
  const { buildLink: buildInternalLink } = useNavigation();
  const { buildLink: buildAgendamentoLink } = useAgendamentoNavigation();

  const searchParams = useSearchParams(Object.values(PersistedUserNavigationSearchParams));
  const cityParam = searchParams.getParam(PersistedUserNavigationSearchParams.CIDADE);

  const userLocation = locationOverride ?? userStore;

  const resolveName = (searchResult: SearchResult): string => {
    if (
      searchResult.searchType === SearchType.DOCTOR
      || searchResult.searchType === SearchType.CLINIC
      || searchResult.searchType === SearchType.BRANCH_LABORATORY
    ) {
      return formatToTitleCase(`${searchResult.displayName}`);
    }
    return `${searchResult.displayName}`;
  };

  const buildExamsUrl = (searchResult: SearchResult): string => {
    const basePath = searchResult.searchType === SearchType.VACCINE ? '/busca/vacina/' : '/busca/exames-laboratoriais/';
    const procedureCodes = searchResult?.code?.split(',').filter(Boolean);
    return buildPathWithQuery(basePath, {
      exames: procedureCodes || [''],
    });
  };

  const resolveLink = useCallback((searchResult: SearchResult, otherParams: Record<string, string> = {}): string => {
    if (!searchResult.slug && !searchResult.code) {
      return '/';
    }

    const linkMap = {
      [SearchType.DOCTOR]: `/profissionais/${searchResult.slug}/`,
      [SearchType.CLINIC]: `/parceiros/${searchResult.slug}/`,
      [SearchType.BRANCH_LABORATORY]: `/laboratorios/${searchResult.slug}/`,
      [SearchType.LABORATORIAL_EXAM]: buildExamsUrl(searchResult),
      [SearchType.VACCINE]: buildExamsUrl(searchResult),
    };

    const slug = searchResult.slug || searchResult.code;
    const pathUrl = linkMap[searchResult.searchType as keyof typeof linkMap] || `/busca/${slug}/`;

    const shouldRedirectToLegacySystem = isProduction && isProcedureSearch(searchResult.searchType as SearchType);

    const city: string | undefined = cityParam ?? userLocation?.city ?? searchResult?.city;
    const overridesCityParams = city ? { [PersistedUserNavigationSearchParams.CIDADE]: city } : undefined;

    const persistLocationJson = userLocation?.formattedAddress && shouldRedirectToLegacySystem
      ? {
          [PersistedUserNavigationSearchParams.LOCATION]: JSON.stringify(userLocation),
        }
      : undefined;

    const overrides = { ...overridesCityParams, ...persistLocationJson };
    const query = Object.keys(otherParams).length > 0 ? otherParams : undefined;

    if (shouldRedirectToLegacySystem) {
      return buildAgendamentoLink({ path: pathUrl, query, overrides });
    }
    return buildInternalLink({ path: pathUrl, query, overrides });
  }, [cityParam, isProduction, userLocation, buildInternalLink, buildAgendamentoLink]);

  const handleSearchExamsUrl = useCallback((isVaccine: boolean, selectedProcedures: SelectedProcedures[]): string => {
    const basePath = isVaccine ? '/busca/vacina/' : '/busca/exames-laboratoriais/';
    const pathUrl = buildPathWithQuery(basePath, {
      exames: selectedProcedures.map((exam) => exam.code),
    });

    const city: string | undefined = userLocation?.city ?? (cityParam ?? undefined);
    const persistedCityParams = city
      ? {
          [PersistedUserNavigationSearchParams.CIDADE]: city,
        }
      : undefined;

    const persistLocationJson = userLocation?.formattedAddress && isProduction
      ? {
          [PersistedUserNavigationSearchParams.LOCATION]: JSON.stringify(userLocation),
        }
      : undefined;

    const overrides = { ...persistedCityParams, ...persistLocationJson };

    if (isProduction) {
      return buildAgendamentoLink({ path: pathUrl, overrides });
    }
    return buildInternalLink({ path: pathUrl, overrides });
  }, [cityParam, isProduction, userLocation, buildInternalLink, buildAgendamentoLink]);

  return {
    isProcedureSearch,
    resolveName,
    resolveLink,
    buildExamsUrl,
    handleSearchExamsUrl,
  };
}
